import { useLayoutEffect, useRef, useState } from "react";

// Measures an element's content-box width *and* height with a ResizeObserver. The image
// crop-cover render (TODO §3.6) needs both dimensions to map a crop rectangle into the
// logical-canvas content window; `useElementWidth` (scale-only) isn't enough there.
//
// Reads `clientWidth`/`clientHeight` — the element's own *layout* size in logical px,
// unaffected by the `scale()` transform on the slide canvas. (The earlier seed used
// `getBoundingClientRect()`, which returns the post-transform *scaled* size and fed a
// wrong-sized box into the placement math on the first frame — visibly mis-placing the
// image in a narrow two-column window until the observer corrected it.)
export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // Seed before paint so the first frame isn't a zero-size one — a layout effect,
    // not `useEffect`, which would run after the browser had already painted it.
    measure();
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}
